import { Dispatch, memo, MouseEvent, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import styles from "./StoryListItem.module.css";
import Icon from "../../common/Icon";
import { decodeUnicode, ImageInfo, isDev, MenusItem, Story } from "../../common/Common";
import Menus from "../../common/Menus";
import UserActivityInfo from "../../common/UserActivityInfo";
import Gallery from "./Gallery";
import DataProvider from "../../../data_provider/DataProvider";
import { BASE_URL, ContentType, DEV_BASE_URL } from "../../../data_provider/Config";
import ImageModal from "./ImageModal";
import useCookies from "react-cookie/cjs/useCookies";

import { useModal } from "../../modal/Modal";
import LoginForm from "../../login/LoginForm";
import SignUpModal from "../../login/SignUpModal";
import ModalButtons from "../../modal/ModalButtons";
import { ToastData, ToastType } from "../../common/Toast";
import { ContentIdx } from "./StoryView";
import StoryPictureGrid from "./StoryPictureGrid";


export interface StoryListItemProps {
    currentUser: string;
    item: Story;
    isActive: boolean;
    isLogin: boolean; // 로그인 여부
    mapId: string;
    xid: number;
    selectedSidRef: React.MutableRefObject<number>;
    selectedIdx: number;
    sort: string;
    backToList: () => void;
    setIsLoading: (isLoading: boolean) => void;
    setStoryImages: Dispatch<SetStateAction<ImageInfo[] | undefined>>;
    setToast?: Dispatch<React.SetStateAction<ToastData | undefined>>;
    setContentIdx: Dispatch<SetStateAction<ContentIdx>>;
    setSelectedIdx: Dispatch<SetStateAction<number>>;
    setOpenMenuId: Dispatch<SetStateAction<number>>;
    moveToGallery: () => void; // 갤러리로 이동하는 함수

    openMapScreenModal?: (modalProps: any) => void; // 지도 화면 모달 열기 함수
    closeMapScreenModal?: () => void; // 지도 화면 모달 닫기 함수
    isAdmin: boolean;
}

// 이미지 옵션
export interface ImageOptions {
    url: string;
    IID: number;
    first?: boolean;
    last?: boolean;
}


export type RelativeOptions = {
    /** 테스트/주입용 현재 시각 (기본: new Date()) */
    now?: Date;
};

export function formatRelativeKR(
    input: string | Date,
    options: RelativeOptions = {}
): string {
    const now = options.now ?? new Date();
    const d = typeof input === "string" ? parseUsDatetime(input) : input;
    if (!d || Number.isNaN(d.getTime())) return "";

    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.max(0, Math.floor(diffMs / 60000));

    if (diffMin < 1) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;

    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}시간 전`;

    return formatDateKR(d);
}

/** "Apr 23, 2025, 1:01:04 PM" 형태를 로컬 타임존 Date로 파싱 */
export function parseUsDatetime(s: string): Date | null {
    if (!s) return null;

    // 좁은 공백(U+202F), NBSP(U+00A0) 등 정규화
    const norm = s.replace(/\u202F|\u00A0/g, " ").replace(/\s+/g, " ").trim();

    // 1) 브라우저 내장 파서가 되면 그대로 사용
    const native = new Date(norm);
    if (!Number.isNaN(native.getTime())) return native;

    // 2) 수동 파싱
    const re =
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),\s*(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/i;
    const m = norm.match(re);
    if (!m) return null;

    const months: Record<string, number> = {
        Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
        Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };

    const mon = months[m[1].slice(0, 3)];
    const day = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    let hour = parseInt(m[4], 10);
    const min = parseInt(m[5], 10);
    const sec = parseInt(m[6], 10);
    const ap = m[7].toUpperCase() as "AM" | "PM";

    if (ap === "PM" && hour !== 12) hour += 12;
    if (ap === "AM" && hour === 12) hour = 0;

    return new Date(year, mon, day, hour, min, sec, 0); // 로컬 타임존
}

/** yyyy년 MM월 dd일 */
export function formatDateKR(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}년 ${mm}월 ${dd}일`;
}

const allowedFileTypes = ["image/png", "image/jpeg", "image/gif"]

const StoryListItem = (props: StoryListItemProps) => {

    const [storyData, setStoryData] = useState({ ...props.item });
    const [isReplyOpen, setIsReplyOpen] = useState(false);
    const [reply, setReply] = useState(false);
    const [cookies] = useCookies(["KMUI"]);

    const formRef = useRef<HTMLFormElement>(null);
    const [images, setImages] = useState<ImageInfo[]>([]);
    const fileUploadRef = useRef<HTMLInputElement>(null);

    const [isMenuActive, setIsMenuActive] = useState<boolean>(false);
    const [isBlind, setIsBlind] = useState<boolean>(false)
    const wrapRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // 옵션 버튼 바깥을 누르는 경우 옵션 버튼 닫기
    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
          if (!wrapRef.current) return;
          const target = e.target as Node;
          if (!wrapRef.current.contains(target)) props.setOpenMenuId(-1);
        };
        document.addEventListener("click", onDocClick as any);
        return () => document.removeEventListener("click", onDocClick as any);
    }, []);

    useEffect(()=>{setIsMenuActive(props.isActive)},[props.isActive])

    useEffect(()=>{
        storyData.reported && setIsBlind(true);
    },[])

    useEffect(() => {
        if (!fileUploadRef.current) return;

        const store = new DataTransfer();
        // 새로 추가한 이미지일 경우 (File type), DB 저장을 위해 form data 에 추가
        images.forEach((image) => image.src instanceof File && store.items.add(image.src));
        fileUploadRef.current.files = store.files;
    }, [images]);

    // 댓글 입력 중 엔터 입력 시 저장이벤트 처리 | 250825 | smyun
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault(); // 엔터키 입력
            submitStory();
        }
    };

    const onClickSignUp = () => {
        props.openMapScreenModal &&
            props.openMapScreenModal({
                title: "회원가입",
                children: <SignUpModal onClose={() => props.closeMapScreenModal && props.closeMapScreenModal()} />,
                modalButtons: null,
                size: "normal",
            });
    };

    const placeholderText = props.isLogin ? 
        (reply && storyData.storyType === 'reply' ? 
            `@${storyData.userName} ` 
            : "댓글을 입력해주세요."
        )
        : 
        "로그인이 필요합니다.";

    const checkLogin = () => {
        if (!props.isLogin) {
            props.openMapScreenModal &&
                props.openMapScreenModal({
                    title: "안내",
                    children: (
                        <>
                            로그인이 필요한 서비스입니다.
                            <br />
                            로그인을 하시겠습니까?
                        </>
                    ),
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () =>
                                        props.openMapScreenModal &&
                                        props.openMapScreenModal({
                                            title: "로그인",
                                            children: (
                                                <LoginForm
                                                    onClose={() => {
                                                        props.closeMapScreenModal && props.closeMapScreenModal();
                                                    }}
                                                    onClickSignUp={onClickSignUp}
                                                />
                                            ),
                                            modalButtons: null,
                                            size: "wide",
                                            disableOverlayClose: true,
                                        }),
                                },
                            ]}
                        />
                    ),
                });
        }
    }

    const submitStory = async () => {

        if (!props.isLogin) {
            props.openMapScreenModal &&
                props.openMapScreenModal({
                    title: "안내",
                    children: (
                        <>
                            로그인이 필요한 서비스입니다.
                            <br />
                            로그인을 하시겠습니까?
                        </>
                    ),
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () =>
                                        props.openMapScreenModal &&
                                        props.openMapScreenModal({
                                            title: "로그인",
                                            children: (
                                                <LoginForm
                                                    onClose={() => {
                                                        props.closeMapScreenModal && props.closeMapScreenModal();
                                                    }}
                                                    onClickSignUp={onClickSignUp}
                                                />
                                            ),
                                            modalButtons: null,
                                            size: "wide",
                                            disableOverlayClose: true,
                                        }),
                                },
                            ]}
                        />
                    ),
                });
        }

        if (!formRef.current) return;

        const form = new FormData(formRef.current);

        if (form.get("content")?.toString().trim() === "") {
            props.setToast && props.setToast({
                type: ToastType.WARNING,
                message: "내용을 입력해주세요.",
            })
            return;
        }


        let childStoryType;
        switch (storyData.storyType) {
            case "comment":
                childStoryType = "reply";
                break;
            case "reply":
                childStoryType = "reply";
                break;
            default : // story
                childStoryType = "comment"
        }

        form.append("storyType", childStoryType);


        let parentSid;

        if (storyData.storyType === "reply") {
            const content = '@'+ storyData.userName + ' ' + form.get("content");
            form.set("content", content)
            parentSid = storyData.parentSid;
        } else {
            parentSid = storyData.SID;
        }

        form.append("parentSid", parentSid.toString());

        if (!fileUploadRef.current) return;

        if (images && images.length > 0) {
            form.getAll("images").forEach((file) => {

                // 시간
                const now = new Date();
                const pad = (num: number, size: number = 2) =>
                    num.toString().padStart(size, "0");
                const year = now.getFullYear();
                const month = pad(now.getMonth() + 1);
                const day = pad(now.getDate());
                const hour = pad(now.getHours());
                const min = pad(now.getMinutes());
                const sec = pad(now.getSeconds());
                const ms = pad(now.getMilliseconds(), 3);
                const imageName = props.mapId + "_" + year + month + day + hour + min + sec + ms + "_" + Math.random() + ".png";
                file instanceof File && form.append("imageNames", imageName);
            });
        }


        try {
            props.setIsLoading(true);
            const res = await DataProvider.AddStory({ mapid: props.mapId, XID: props.xid.toString() }, form);
        } catch (err) {
            console.log(err);
        }

        props.setIsLoading(false);
        setImages([]);
        if (fileUploadRef.current) {
            fileUploadRef.current.value = "";
        }
        formRef.current.reset();
    }

    const onFileAdded = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.currentTarget.files) return;
        let files = Array.from(e.currentTarget.files);

        // 새 이미지 추가
        const newImages = files.map((image: File) => {
            return {
                src: image,
            };
        }) as ImageInfo[];

        if (newImages) {
            setImages([...newImages]);
        }

        // setHover(false);

    };

    const onFileUploadClick = () => {
        fileUploadRef.current && fileUploadRef.current.click();
    };


    const updateLike = async () => {
        try {
            if (!props.isLogin) {
                props.openMapScreenModal &&
                    props.openMapScreenModal({
                        title: "안내",
                        children: (
                            <>
                                로그인이 필요한 서비스입니다.
                                <br />
                                로그인을 하시겠습니까?
                            </>
                        ),
                        modalButtons: (
                            <ModalButtons
                                buttonCount={1}
                                buttons={[
                                    {
                                        label: "확인",
                                        color: "Primary",
                                        onClick: () =>
                                            props.openMapScreenModal &&
                                            props.openMapScreenModal({
                                                title: "로그인",
                                                children: (
                                                    <LoginForm
                                                        onClose={() => {
                                                            props.closeMapScreenModal && props.closeMapScreenModal();
                                                        }}
                                                        onClickSignUp={onClickSignUp}
                                                    />
                                                ),
                                                modalButtons: null,
                                                size: "wide",
                                                disableOverlayClose: true,
                                            }),
                                    },
                                ]}
                            />
                        ),
                    });
            }
            const res = await DataProvider.UpdateUserActivity({
                mapid: props.mapId,
                XID: props.xid.toString(),
                SID: storyData.SID.toString()
            })
            if (res.flag) {
                setStoryData({ ...storyData, like: !storyData.like, likeCnt: storyData.like ? storyData.likeCnt - 1 : storyData.likeCnt + 1 });
            }
        } catch (err) {
            console.log("좋아요 업데이트 실패. 에러 내용 ", err);
        }



        props.backToList();
    };

    const storyMenus = useMemo<MenusItem[]>(() => [
        {
          title: "신고",
          onClickItem: () => {
            setIsMenuActive(false)
            props.setSelectedIdx(storyData.SID)
            props.setContentIdx(ContentIdx.REPORT)
        }
        },
        {
          title: "삭제",
          onClickItem: () => { 
            setIsMenuActive(false);
            props.setSelectedIdx(storyData.SID);
            props.setContentIdx(ContentIdx.DELETE);
        },
        },
      ], []);

    const getStoryMenu = () => {

        if (cookies.KMUI === undefined){
            return [];
        }

        // 251029 | smyun | 관리자인 경우 삭제 버튼 보이도록 수정
        const isAuthor = storyData.userId === cookies.KMUI;

        // 🔹 제외할 메뉴를 동적으로 결정
        const excluded: string[] = [];
        if (isAuthor) excluded.push("신고");       // 작성자면 신고 제외
        if (!props.isAdmin && !isAuthor) excluded.push("삭제"); // 일반유저면 삭제 제외

        // 🔹 제외 대상이 있으면 필터링, 없으면 전체 반환
        return excluded.length
            ? storyMenus.filter(menu => !excluded.includes(menu.title))
            : storyMenus;
    };

    const onClickMenus = (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                checkLogin();
                props.setOpenMenuId(props.isActive? -1 : storyData.SID);
        }

    return (

            <div className={`${styles.wrapper} ${storyData.storyType === 'reply' && styles.replyContainer}`}>
                {storyData.storyType !== 'story' && <Icon name={"arrowDownRight"} color="black" size="small" style={{ marginRight: "10px" }} />}
                <div className={styles.container}>
                    <div className={styles.header}>
                        <div style={{ display: "flex" }}>
                            <span className={styles.name}>{storyData.userName}</span>
                            <span className={`${styles.date}`}>{formatRelativeKR(storyData.createDate)}</span>
                        </div>
                        <div ref={wrapRef} className={styles.button} onClick={onClickMenus}>
                            <Icon name={"more"} color="grey" />
                        </div>
                        {isMenuActive && <Menus options={getStoryMenu()} />}
                    </div>
                    <div className={styles.content}>
                        <div
                            className={`${styles.text} ${!isReplyOpen ? styles.clamp : ""}`}
                        >
                            {isBlind? 
                                <div className={styles.blindText}>
                                    <span>신고한 이야기입니다.</span>
                                    <div className={styles.showButton} onClick={()=>{setIsBlind(false)}}>
                                        확인하기
                                    </div>
                                </div>
                                : 
                                <span>{storyData.content}</span>
                            }
                        </div>
                        {storyData.content.length > 70 &&
                            <button
                                type="button"
                                className={styles.link}
                                onClick={() => setIsReplyOpen(v => !v)}
                                aria-expanded={isReplyOpen}
                            >
                                {isReplyOpen ? "접기" : "더보기"}
                            </button>
                        }

                    </div>
                    
                    {/* 이미지 영역 */}
                    {storyData.images && storyData.images.length > 0 && <StoryPictureGrid images={storyData.images} storyType={storyData.storyType}/>}

                    <div className={styles.footer}>
                        {/* 사용자 액션 영역 */}
                        <div className={styles.actions}>
                            <UserActivityInfo
                                size="small"
                                count={storyData.likeCnt}
                                type="favorite"
                                label="좋아요"
                                iconStyle={{ color: storyData.like ? "default" : "lightGrey" }}
                                onClickIcon={updateLike}
                            />
                            {storyData.storyType !== 'story' &&
                                <div
                                    onClick={() => {
                                        setReply(prev => {
                                        const next = !prev;
                                        if (next) {
                                            // 다음 렌더 후 포커스
                                            setTimeout(() => inputRef.current?.focus(), 0);
                                        }
                                        return next;
                                        });
                                    }}
                                >
                                    <span className={styles.inputCollapse}>{reply ? "접기" : "답글 달기"}</span>
                                </div>
                            }
                        </div>
                        {storyData.storyType && (storyData.storyType === 'story' || reply) &&
                            <form ref={formRef} >
                                <div className={styles.inputContainer}>
                                    <input ref={inputRef} name={"content"} className={styles.input} onFocus={checkLogin} readOnly={!props.isLogin} onKeyDown={handleKeyDown} placeholder={placeholderText} />
                                    <div className={styles.inputButton} onClick={onFileUploadClick} style={images && images.length > 0 ? { height: "50px" } : {}}>
                                        {
                                            images && images.length > 0 ? (
                                                <img
                                                    src={
                                                        images[0].src instanceof File
                                                            ? URL.createObjectURL(images[0].src as Blob)
                                                            : (images[0].src as string).replaceAll("\\", "/")
                                                    }
                                                    style={{"padding": "10px"}}
                                                    onClick={() => setImages([])}
                                                />
                                            ) : (
                                                <Icon name={"addPhoto2"} color="grey" />
                                            )
                                        }
                                    </div>
                                    <div className={styles.inputButton} onClick={submitStory}>
                                        <Icon name={"check"} color="grey" />
                                    </div>
                                </div>
                                <input className={styles.imageInput}
                                    ref={fileUploadRef}
                                    name={"images"}
                                    accept={allowedFileTypes.join(",")}
                                    type="file"
                                    onChange={onFileAdded} />
                            </form>
                        }
                    </div>
                </div>
            </div>
    );
};

export default memo(StoryListItem, (prev, next)=> {
    // if (prev.sort !== next.sort) return true;
    if (prev.isActive !== next.isActive) return false;

    return true;
})

// export default StoryListItem;
